/**
 * Safetrax ROS Protocol Handler
 * Implements serial communication protocol for device configuration and monitoring
 */

import { SerialConnection } from './serial.js';
import {
  delay,
  parseSensorData,
  bytesToHex,
  hexToBytes,
  getConfigByteCount,
  getChecksumIndex
} from './utils.js';

// Protocol patterns (matching legacy implementation)
const MATCH_LOGIN = /^Fleetpin ROS V/;
const MATCH_SAMPLE_IMU = /^S([A-Fa-f0-9]{4}(?: [A-Fa-f0-9]{4}){11})$/;
const MATCH_INIT = /\nint$/;
// Pattern to extract firmware version from banner (e.g., "Fleetpin ROS V1.50 01 Aug 2024")
const MATCH_VERSION = /^Fleetpin ROS V(\d+\.\d+)/;

export class SafetraxProtocol {
  constructor() {
    this.serial = new SerialConnection();
    this.isLoggedIn = false;
    this.firmwareVersion = null; // Firmware version string (e.g., "1.50", "1.55", "1.58")
    this.pendingResponse = null;
    this.responseTimeout = null;
    this.responseTimeoutMs = 5000; // 5 seconds
    this.loginPromise = null; // Promise that resolves when login banner is received
    this.isSampling = false;
    this.sampleCallback = null;
    this.isCriticalOperation = false; // Flag to prevent background operations during config writes
    this.lastRxAt = 0; // Timestamp of last received serial chunk
    
    // Set up data listener
    this.serial.on('data', this.handleData.bind(this));
    
    // Set up connect listener - login immediately when connected
    this.serial.on('connect', () => {
      console.log('Serial connected');
    });
    
    this.serial.on('disconnect', () => {
      this.isLoggedIn = false;
      this.isSampling = false;
      
      // Reject any pending login promise
      if (this.loginPromise) {
        this.loginPromise.reject(new Error('Disconnected during login'));
        this.loginPromise = null;
      }
    });
  }

  /**
   * Handle incoming data
   * @param {string} data - Incoming data line
   */
  handleData(data) {
    this.lastRxAt = Date.now();

    // Log all incoming data when waiting for response (for debugging)
    if (this.pendingResponse) {
      console.log('[Protocol.handleData] Received while waiting for response:', JSON.stringify(data),
                  'charCodes:', Array.from(data).map(c => '0x' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(' '));
    }
    
    // Check for login banner
    if (data.match(MATCH_LOGIN)) {
      console.log('✓ Device logged in, received banner:', data);
      this.isLoggedIn = true;
      
      // Parse firmware version from banner (e.g., "Fleetpin ROS V1.50 01 Aug 2024")
      const versionMatch = data.match(MATCH_VERSION);
      if (versionMatch) {
        this.firmwareVersion = versionMatch[1];
        console.log(`✓ Detected firmware version: ${this.firmwareVersion}`);
      } else {
        console.warn('Could not parse firmware version from banner, defaulting to v1.55 behavior');
        this.firmwareVersion = '1.55'; // Default to v1.55 if parsing fails
      }
      
      return;
    }
    
    // Check for init sequence (device was reset)
    if (data.match(MATCH_INIT)) {
      console.log('Device init detected, logging in...');
      this.login();
      return;
    }
    
    // Check for IMU sample data
    // BUT: if we're waiting for a response, handle that instead of streaming
    if (data.match(MATCH_SAMPLE_IMU) && !this.pendingResponse) {
      const sensorData = parseSensorData(data);
      if (sensorData && this.sampleCallback) {
        this.sampleCallback(sensorData);
      }
      // Request next sample if streaming
      if (this.isSampling) {
        this.requestNextSample();
      }
      return;
    }
    
    // Handle pending response (for config commands, battery, etc.)
    if (this.pendingResponse) {
      // Ignore background IMU stream frames while waiting for non-IMU command responses.
      // Keep sampling alive by immediately requesting the next sample.
      const isImuSample = data.match(MATCH_SAMPLE_IMU);
      if (isImuSample && !this.pendingResponse.includeImu) {
        if (this.isSampling) {
          this.requestNextSample();
        }
        return;
      }

      // Check if we have a matcher for immediate resolution
      const matcher = this.pendingResponse.matcher;
      let shouldResolveImmediately = false;
      let matchedData = data;
      
      // Check matcher (for binary responses like ACK/NAK)
      if (matcher) {
        if (typeof matcher === 'function') {
          // Matcher is a function - call it with the data
          shouldResolveImmediately = matcher(data);
        } else if (typeof matcher === 'number') {
          // Matcher is a byte value - check if data starts with that byte
          const firstByte = data.charCodeAt(0);
          shouldResolveImmediately = firstByte === matcher;
        }
      }
      
      if (shouldResolveImmediately) {
        // Immediate resolution for matched response (e.g., ACK/NAK)
        console.log('[Protocol.handleData] Matcher matched! Resolving immediately with:', JSON.stringify(data),
                    'charCodes:', Array.from(data).map(c => '0x' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(' '));
        
        const resolveFunc = this.pendingResponse.resolve;
        
        // Clear all timeouts
        if (this.pendingResponse.accumulateTimeout) {
          clearTimeout(this.pendingResponse.accumulateTimeout);
        }
        if (this.responseTimeout) {
          clearTimeout(this.responseTimeout);
        }
        
        // Clear references BEFORE resolving
        this.pendingResponse = null;
        this.responseTimeout = null;
        
        // Resolve with the raw data (don't trim binary responses)
        resolveFunc(data);
        return;
      }
      
      // If this response is matcher-driven (e.g. waiting for ACK/NAK),
      // ignore unmatched lines/chunks instead of accumulating and auto-resolving.
      // This prevents unrelated traffic (like battery command echoes) from
      // completing the wrong pending response.
      if (matcher) {
        return;
      }

      // No immediate match - accumulate data with newlines to preserve structure
      if (!this.pendingResponse.buffer) {
        this.pendingResponse.buffer = '';
      }
      this.pendingResponse.buffer += data + '\n';
      console.log('[Protocol.handleData] Accumulated buffer:', JSON.stringify(this.pendingResponse.buffer));
      
      // Count hex words in buffer (rough estimate for S command)
      const hexWords = this.pendingResponse.buffer.match(/[0-9A-Fa-f]{4}/g);
      const wordCount = hexWords ? hexWords.length : 0;
      
      // Reset the accumulation timeout
      if (this.pendingResponse.accumulateTimeout) {
        clearTimeout(this.pendingResponse.accumulateTimeout);
      }
      
      // Detect end of response:
      // - S command: has 12 hex words
      // - Other responses: data stops arriving for 200ms
      const isSensorData = data.match(/^S[0-9A-Fa-f\s]+$/);
      const shouldResolveAccumulated = isSensorData && wordCount >= 12;
      
      if (shouldResolveAccumulated) {
        // Immediately resolve sensor data
        const resolveFunc = this.pendingResponse.resolve;
        const buffer = this.pendingResponse.buffer.trim();
        
        // Clear all timeouts
        if (this.pendingResponse.accumulateTimeout) {
          clearTimeout(this.pendingResponse.accumulateTimeout);
        }
        if (this.responseTimeout) {
          clearTimeout(this.responseTimeout);
        }
        
        // Clear references BEFORE resolving
        this.pendingResponse = null;
        this.responseTimeout = null;
        
        // Now resolve
        resolveFunc(buffer);
      } else {
        // Wait for more data to stop arriving (longer window for battery command)
        this.pendingResponse.accumulateTimeout = setTimeout(() => {
          if (this.pendingResponse) {
            this.pendingResponse.resolve(this.pendingResponse.buffer.trim());
            clearTimeout(this.responseTimeout);
            this.pendingResponse = null;
            this.responseTimeout = null;
          }
        }, 250); // 250ms accumulation window for multi-line responses
      }
    }
  }

  /**
   * Wait until serial traffic is quiet and there is no active pending response.
   * Used before critical config operations to avoid interference from in-flight
   * IMU/battery responses.
   *
   * @param {number} maxWaitMs
   * @param {number} quietMs
   * @returns {Promise<void>}
   */
  async waitForSerialQuiet(maxWaitMs = 3000, quietMs = 300) {
    const start = Date.now();

    while (Date.now() - start < maxWaitMs) {
      const noPending = !this.pendingResponse;
      const idleFor = Date.now() - this.lastRxAt;
      const isQuiet = this.lastRxAt === 0 || idleFor >= quietMs;

      if (noPending && isQuiet) {
        return;
      }

      await delay(25);
    }

    throw new Error('Serial bus not quiet; pending/background response still active');
  }
  
  /**
   * Request next sensor sample
   */
  async requestNextSample() {
    if (!this.isLoggedIn || !this.isSampling) {
      return;
    }
    try {
      await this.serial.writeCommand('s');
    } catch (err) {
      console.error('Failed to request sample:', err);
    }
  }

  /**
   * Wait for response with timeout
   * @param {Function|number|null} matcher - Optional matcher for the response:
   *   - Function: called with data, should return true if response is complete
   *   - Number: byte value to match (e.g., 0x06 for ACK)
   *   - null/undefined: use default accumulation behavior
   * @param {number} timeoutMs - Optional timeout in ms (default: this.responseTimeoutMs)
   * @returns {Promise<string>}
   */
  waitForResponse(matcher = null, timeoutMs = null, options = {}) {
    return new Promise((resolve, reject) => {
      this.pendingResponse = {
        resolve,
        reject,
        buffer: '',
        accumulateTimeout: null,
        matcher: matcher,
        includeImu: options.includeImu === true
      };
      
      const timeout = timeoutMs !== null ? timeoutMs : this.responseTimeoutMs;
      
      this.responseTimeout = setTimeout(() => {
        if (this.pendingResponse) {
          if (this.pendingResponse.accumulateTimeout) {
            clearTimeout(this.pendingResponse.accumulateTimeout);
          }
          // If we have partial data, resolve with it; otherwise reject
          if (this.pendingResponse.buffer) {
            resolve(this.pendingResponse.buffer.trim());
            this.pendingResponse = null;
          } else {
            this.pendingResponse = null;
            reject(new Error('Response timeout'));
          }
        }
        // else: already resolved by handleData, nothing to do
      }, timeout);
    });
  }

  /**
   * Connect to device
   * @returns {Promise<boolean>}
   */
  async connect() {
    // Reset session state for a fresh handshake on every connect/reconnect
    this.isLoggedIn = false;
    this.firmwareVersion = null;

    const connected = await this.serial.connect();
    if (!connected) {
      return false;
    }

    // Perform robust login handshake with retries.
    // This handles reconnect-after-reload where the MCU may still be booting
    // when the first login sequence is sent.
    const loggedIn = await this.loginWithRetry();
    if (!loggedIn) {
      throw new Error('Unable to login to device after connect');
    }

    return true;
  }

  /**
   * Wait for login banner to be observed by handleData()
   * @param {number} timeoutMs
   * @returns {Promise<boolean>}
   */
  async waitForLoginBanner(timeoutMs = 2500) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this.isLoggedIn) {
        return true;
      }
      await delay(50);
    }
    return false;
  }

  /**
   * Attempt login multiple times to handle device boot timing on reconnect.
   * @param {number} maxAttempts
   * @returns {Promise<boolean>}
   */
  async loginWithRetry(maxAttempts = 4) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`[Login] Attempt ${attempt}/${maxAttempts}`);

      // Nudge CLI state machine before login sequence.
      try {
        await this.serial.write('\r\n');
      } catch {
        // Ignore warm-up write failures; login command below is authoritative.
      }

      await delay(50);
      this.login();

      // First attempt gets longer timeout to allow reset/boot banner.
      const timeoutMs = attempt === 1 ? 3000 : 1800;
      const ok = await this.waitForLoginBanner(timeoutMs);
      if (ok) {
        return true;
      }

      await delay(250);
    }

    return false;
  }

  /**
   * Disconnect from device
   */
  async disconnect() {
    this.isLoggedIn = false;
    this.isSampling = false;
    
    // Clear any pending response handlers
    if (this.pendingResponse) {
      if (this.pendingResponse.accumulateTimeout) {
        clearTimeout(this.pendingResponse.accumulateTimeout);
      }
      this.pendingResponse = null;
    }
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }
    
    await this.serial.disconnect();
  }

  /**
   * Login to device (send 'q avr ' sequence for legacy firmware)
   * Non-blocking - response will be handled in handleData
   */
  login() {
    console.log('Logging in...');
    this.serial.write('q avr ').catch((err) => {
      console.error('Login write failed:', err);
    });
  }

  /**
   * Read configuration from device (U command)
   * Version-aware: returns 49 bytes for v1.50, 57 bytes for v1.55+
   * @returns {Promise<Uint8Array>} Config bytes (version-specific)
   */
  async readConfig() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      // Determine expected byte count based on firmware version
      const expectedByteCount = getConfigByteCount(this.firmwareVersion);
      console.log(`[ReadConfig] Firmware version: ${this.firmwareVersion || 'unknown (defaulting to v1.55)'}`);
      console.log(`[ReadConfig] Expected config bytes: ${expectedByteCount}`);
      
      await this.serial.writeCommand('U');
      
      // Wait for response
      const response = await this.waitForResponse();
      
      // Debug: log the raw response
      console.log('[ReadConfig] Raw response from device:', response);
      console.log('[ReadConfig] Response length:', response.length);
      
      // Remove command echo and whitespace
      let hexString = response.replace(/\s/g, '');
      if (hexString.startsWith('U')) {
        hexString = hexString.substring(1);
      }
      
      console.log('[ReadConfig] After removing spaces and command:', hexString);
      console.log('[ReadConfig] Hex string length:', hexString.length);
      
      // Parse hex response
      const bytes = hexToBytes(hexString);
      
      console.log(`[ReadConfig] ✓ Parsed ${bytes.length} bytes from device`);
      
      // The device sends parameter bytes and may also send calibration bytes
      // For v1.50: 49 parameter bytes + 17 calibration bytes
      // For v1.55: 57 parameter bytes + 17 calibration bytes
      if (bytes.length < expectedByteCount) {
        throw new Error(`Invalid config length: expected at least ${expectedByteCount} bytes, got ${bytes.length}`);
      }
      
      // Extract only the parameter bytes (version-specific)
      const paramBytes = bytes.slice(0, expectedByteCount);
      
      console.log(`[ReadConfig] ✓ Config read from device (${expectedByteCount} parameter bytes for v${this.firmwareVersion || '1.55'})`);
      return paramBytes;
    } catch (err) {
      console.error('[ReadConfig] Failed:', err);
      throw new Error(`Failed to read config: ${err.message}`);
    }
  }

  /**
   * Write configuration to device (V command)
   * IMPORTANT: This requires sampling to be paused first!
   * Use writeConfigSafe() instead to automatically pause/resume sampling.
   *
   * Protocol sequence (from ros.ino):
   * 1. Send ']' to enable special mode (sets bco_special = true)
   * 2. Send 'V' command
   * 3. Device echoes 'V'
   * 4. Device sends '<' to indicate ready for data transfer
   * 5. Send all config bytes (49 for v1.50, 57 for v1.55+, including checksum)
   * 6. Device sends ACK (0x06) if checksum valid, NAK (0x15) if invalid
   *
   * @param {Uint8Array} configBytes - Config bytes (version-specific)
   * @returns {Promise<void>}
   */
  async writeConfig(configBytes) {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    // Validate config byte count matches firmware version
    const expectedByteCount = getConfigByteCount(this.firmwareVersion);
    if (configBytes.length !== expectedByteCount) {
      throw new Error(`Invalid config length for v${this.firmwareVersion || '1.55'}: expected ${expectedByteCount} bytes, got ${configBytes.length}`);
    }

    try {
      console.log(`[WriteConfig] Starting config write sequence for v${this.firmwareVersion || '1.55'}...`);
      console.log(`[WriteConfig] Sending ${expectedByteCount} bytes (${expectedByteCount - 1} params + 1 checksum)`);
      console.log('[WriteConfig] Config bytes (hex):', Array.from(configBytes).map(b => b.toString(16).padStart(2, '0')).join(' '));
      
      // Step 1: Enable special mode (required for V command)
      // NOTE: ] command does NOT send a response (just sets bco_special = true)
      console.log('[WriteConfig] Step 1: Sending "]" to enable special mode...');
      await this.serial.writeCommand(']');
      console.log('[WriteConfig] "]" sent, waiting 50ms for device to process...');
      await delay(50);
      
      // Step 2: Send 'V' command to initiate config download
      // Device will respond with 'V' echo and '<' (no newline)
      console.log('[WriteConfig] Step 2: Sending "V" command...');
      const responsePromise = this.waitForResponse(
        // Wait specifically for device-ready marker. Ignore unrelated traffic.
        (data) => data.includes('<'),
        2000
      );
      await this.serial.writeCommand('V');
      console.log('[WriteConfig] "V" sent, waiting for device response (V<)...');
      
      // Step 3 & 4: Wait for device to echo 'V' and send '<'
      const response = await responsePromise;
      console.log('[WriteConfig] Device response received:', JSON.stringify(response));
      console.log('[WriteConfig] Response length:', response.length, 'bytes:', Array.from(response).map(c => c.charCodeAt(0)));
      
      // Check if device sent '<' (ready for data)
      if (!response.includes('<')) {
        throw new Error(`Device not ready for config (expected '<', got: ${JSON.stringify(response)})`);
      }
      
      console.log('[WriteConfig] ✓ Device ready (received "<")');
      console.log(`[WriteConfig] Step 3: Sending ${expectedByteCount} config bytes...`);
      
      // Debug: Check configBytes type and content
      console.log('[WriteConfig] configBytes type:', configBytes.constructor.name);
      console.log('[WriteConfig] configBytes length:', configBytes.length);
      console.log('[WriteConfig] First 10 bytes:', Array.from(configBytes.slice(0, 10)));
      
      // Step 5: Send all config bytes including checksum (version-specific count)
      // CRITICAL: Device's DbgGetByte() expects each byte as TWO HEX ASCII characters
      // Example: 0xFF must be sent as "FF" (two ASCII chars: 0x46, 0x46)
      // IMPORTANT: Use Array.from() to ensure Uint8Array converts properly
      const hexString = Array.from(configBytes)
        .map(b => b.toString(16).padStart(2, '0').toUpperCase())
        .join('');
      
      console.log(`[WriteConfig] Sending ${hexString.length} HEX ASCII chars for ${expectedByteCount} bytes`);
      console.log(`[WriteConfig] HEX data (first 50 chars): ${hexString.substring(0, 50)}...`);

      // Step 6: Arm ACK/NAK listener BEFORE sending payload to avoid race conditions
      console.log('[WriteConfig] Step 4: Arming ACK/NAK listener...');
      const ackResponsePromise = this.waitForResponse(
        (data) => Array.from(data).some((c) => {
          const byte = c.charCodeAt(0);
          return byte === 0x06 || byte === 0x15;
        }),
        5000 // 5 second timeout
      );
      
      // Send as ASCII text string, not raw binary bytes.
      // IMPORTANT: pace transmission to avoid overrunning firmware UART receive while
      // it is echoing each received character in DbgGetChar(true).
      const CHUNK_SIZE = 16; // 8 config bytes worth of HEX chars
      for (let i = 0; i < hexString.length; i += CHUNK_SIZE) {
        const chunk = hexString.slice(i, i + CHUNK_SIZE);
        await this.serial.write(chunk);
        // Small inter-chunk gap improves reliability on legacy firmware receive loop
        await delay(5);
      }
      console.log(`[WriteConfig] ${hexString.length} HEX ASCII chars sent (paced), waiting for ACK/NAK...`);
      
      // Step 7: Wait for ACK (0x06) or NAK (0x15)
      const ackResponse = await ackResponsePromise;
      
      console.log('[WriteConfig] ACK/NAK response received:', JSON.stringify(ackResponse));
      console.log('[WriteConfig] Response charCodes:', Array.from(ackResponse).map(c => '0x' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(' '));
      
      const ackByte = Array.from(ackResponse)
        .map(c => c.charCodeAt(0))
        .find(b => b === 0x06 || b === 0x15);

      if (ackByte === 0x15) { // NAK
        throw new Error('Device sent NAK (0x15 - checksum verification failed)');
      } else if (ackByte !== 0x06) { // ACK
        throw new Error(`Unexpected response (expected ACK=0x06/NAK=0x15, got: ${JSON.stringify(ackResponse)})`);
      }
      
      console.log('✓ Config written to device RAM (checksum verified, received ACK=0x06)');
    } catch (err) {
      console.error('[WriteConfig] FAILED:', err);
      throw new Error(`Failed to write config: ${err.message}`);
    }
  }

  /**
   * Program EEPROM (P command)
   * Writes config from RAM to EEPROM (master & backup)
   * IMPORTANT: This requires sampling to be paused first!
   * Use writeConfigSafe() instead to automatically pause/resume sampling.
   *
   * Protocol sequence (from ros.ino):
   * 1. Send ']' to enable special mode (bco_special was cleared after V command)
   * 2. Send 'P' command
   * 3. Device writes to EEPROM:
   *    - v1.50: Single EEPROM block (~200ms)
   *    - v1.55+: Dual EEPROM blocks (master & backup, ~400ms)
   * 4. Device echoes 'P' when complete
   *
   * @returns {Promise<void>}
   */
  async programEEPROM() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      console.log(`[ProgramEEPROM] Enabling special mode for v${this.firmwareVersion || '1.55'}...`);
      // Step 1: Enable special mode (required for P command)
      await this.serial.writeCommand(']');
      await delay(50);
      
      console.log('[ProgramEEPROM] Sending P command...');
      // Step 2: Send 'P' command and wait for response
      const responsePromise = this.waitForResponse();
      await this.serial.writeCommand('P');
      
      // Step 3 & 4: Wait for device to write EEPROM and echo 'P'
      // v1.50: Single EEPROM write ~200ms
      // v1.55+: Dual EEPROM write ~400ms (master + backup)
      const originalTimeout = this.responseTimeoutMs;
      const versionNum = parseFloat(this.firmwareVersion || '1.55');
      const isV155OrLater = versionNum >= 1.55;
      this.responseTimeoutMs = isV155OrLater ? 1500 : 1000; // Longer timeout for dual EEPROM
      
      console.log(`[ProgramEEPROM] Waiting for EEPROM write (timeout: ${this.responseTimeoutMs}ms)...`);
      
      const response = await responsePromise;
      
      // Restore timeout
      this.responseTimeoutMs = originalTimeout;
      
      console.log('[ProgramEEPROM] Device response:', response);
      
      if (response && response.includes('P')) {
        console.log('✓ Config programmed to EEPROM (master & backup)');
      } else {
        console.warn('Unexpected EEPROM programming response:', response);
        throw new Error(`Unexpected response: ${response}`);
      }
    } catch (err) {
      console.error('Program EEPROM failed:', err);
      throw new Error(`Failed to program EEPROM: ${err.message}`);
    }
  }

  /**
   * Write configuration to device with automatic sampling pause/resume
   * This is the SAFE way to write config - use this instead of writeConfig() directly.
   *
   * Sequence:
   * 1. Pause IMU sampling
   * 2. Write config to RAM (V command)
   * 3. Program to EEPROM (P command)
   * 4. Resume IMU sampling (even if errors occur)
   *
   * @param {Uint8Array} configBytes - Config bytes (version-specific)
   * @returns {Promise<void>}
   */
  async writeConfigSafe(configBytes) {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    // Validate config byte count matches firmware version
    const expectedByteCount = getConfigByteCount(this.firmwareVersion);
    if (configBytes.length !== expectedByteCount) {
      throw new Error(`Invalid config length for v${this.firmwareVersion || '1.55'}: expected ${expectedByteCount} bytes, got ${configBytes.length}`);
    }

    console.log('[WriteConfigSafe] Pausing ALL background operations...');
    
    // Save sampling state
    const wasSampling = this.isSampling;
    const originalCallback = this.sampleCallback;
    
    try {
      // Set critical operation flag to prevent battery reads and other background operations
      this.isCriticalOperation = true;
      
      // Pause IMU sampling to avoid interference with config write sequence
      this.isSampling = false;
      this.sampleCallback = null;
      
      // Wait for any pending IMU/battery responses to complete and drain
      await this.waitForSerialQuiet(4000, 300);
      
      console.log('[WriteConfigSafe] Writing config to device RAM...');
      // Write config to RAM (V command sequence)
      await this.writeConfig(configBytes);
      
      console.log('[WriteConfigSafe] Programming EEPROM...');
      // Program to EEPROM (P command sequence)
      await this.programEEPROM();
      
      console.log('[WriteConfigSafe] Config write complete');
      
    } catch (err) {
      console.error('[WriteConfigSafe] Error:', err);
      throw err;
      
    } finally {
      // Always clear critical operation flag and resume sampling, even if write fails
      this.isCriticalOperation = false;
      this.sampleCallback = originalCallback;
      this.isSampling = wasSampling;
      
      if (this.isSampling && this.isLoggedIn) {
        console.log('[WriteConfigSafe] Resuming IMU sampling and background operations...');
        await delay(100); // Brief delay before resuming
        this.requestNextSample();
      }
    }
  }

  /**
   * Read sensor data (S command)
   * @returns {Promise<object>} Parsed sensor data (pitch, roll)
   */
  async readSensorData(timeoutMs = null) {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      // Set up response handler BEFORE sending command
      const responsePromise = this.waitForResponse(null, timeoutMs, { includeImu: true });
      
      // Now send the command
      await this.serial.writeCommand('S');
      
      // Wait for response
      const response = await responsePromise;

      // Awake probes can legitimately receive sleep/noise markers (e.g. "*", "?", "Zzz").
      // Only parse when we can find a full valid IMU frame to avoid warning spam.
      const lines = String(response)
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

      const fullImuLine = lines.find(line =>
        /^S([A-Fa-f0-9]{4}(?:\s+[A-Fa-f0-9]{4}){11})$/.test(line)
      );

      if (!fullImuLine) {
        return null;
      }

      return parseSensorData(fullImuLine);
    } catch (err) {
      console.error('Read sensor data failed:', err);
      return null;
    }
  }

  /**
   * Read battery voltage (T command)
   * Returns battery voltage and switched battery voltage
   * @returns {Promise<object>} Battery data {battery, swBattery} in volts
   */


  /**
   * Read battery voltage (T command)
   * Returns battery voltage and switched battery voltage
   * @returns {Promise<object>} Battery data {battery, swBattery} in volts
   */
  async readBatteryData() {
    if (this.isCriticalOperation) {
      return { battery: 0, swBattery: 0 };
    }

    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      // Increase timeout for battery read (it sends multiple lines)
      const originalTimeout = this.responseTimeoutMs;
      this.responseTimeoutMs = 10000; // 10 seconds
      
      // Set up response handler BEFORE sending command
      const responsePromise = this.waitForResponse();
      
      // Now send the command
      await this.serial.writeCommand('t');
      
      // Wait for response
      const response = await responsePromise;
      
      // Restore original timeout
      this.responseTimeoutMs = originalTimeout;
      
      // Response will accumulate multiple lines (T, BattV, SWB+D, Vref, Reset, EEstat)
      const battMatch = response.match(/BattV=0x([0-9A-Fa-f]+)/i);
      const swbMatch = response.match(/SWB\+D=0x([0-9A-Fa-f]+)/i);
      
      const battery = battMatch ? parseInt(battMatch[1], 16) / 100 : 0;
      const swBattery = swbMatch ? parseInt(swbMatch[1], 16) / 100 : 0;
      
      return { battery, swBattery };
    } catch (err) {
      console.error('Read battery data failed:', err);
      // Don't swallow the error silently - but return zeros as fallback
      return { battery: 0, swBattery: 0 };
    }
  }

  /**
   * Start streaming sensor data
   * Uses continuous IMU sampling (like legacy implementation)
   * @param {Function} callback - Callback for sensor data
   * @param {number} intervalMs - Not used (kept for API compatibility)
   * @returns {Function} Stop function
   */
  startStreaming(callback, intervalMs = 200) {
    if (this.isSampling) {
      console.warn('Already streaming');
      return () => {};
    }
    
    this.isSampling = true;
    this.sampleCallback = callback;
    
    // Store original callback
    const originalCallback = callback;
    
    // Wrap callback to pass through data
    this.sampleCallback = (sensorData) => {
      originalCallback(sensorData);
    };
    
    // Start the continuous sampling loop
    if (this.isLoggedIn) {
      this.requestNextSample();
    }
    
    // Return stop function
    return () => {
      this.isSampling = false;
      this.sampleCallback = null;
    };
  }
  
  /**
   * Read battery voltage manually (pauses IMU sampling)
   * @returns {Promise<object>} Battery data {battery, swBattery} in volts
   */
  async readBatteryManual() {
    if (this.isCriticalOperation) {
      return { battery: 0, swBattery: 0 };
    }

    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }
    
    console.log('[Battery Manual] Pausing IMU sampling...');
    
    try {
      // Temporarily pause IMU sampling while reading battery
      const wasSampling = this.isSampling;
      this.isSampling = false;
      
      // Clear sample callback temporarily to avoid interference
      const originalCallback = this.sampleCallback;
      this.sampleCallback = null;
      
      // Wait longer for any pending IMU response to complete and drain
      await delay(300);
      
      console.log('[Battery Manual] Reading battery data...');
      const batteryData = await this.readBatteryData();
      console.log('[Battery Manual] Battery data received:', batteryData);
      
      // Restore sample callback
      this.sampleCallback = originalCallback;
      
      // Resume IMU sampling
      this.isSampling = wasSampling;
      if (this.isSampling && this.isLoggedIn) {
        console.log('[Battery Manual] Resuming IMU sampling...');
        this.requestNextSample();
      }
      
      return batteryData;
    } catch (err) {
      console.error('[Battery Manual] Error:', err);
      // Make sure to resume sampling even if battery read fails
      if (!this.isSampling) {
        this.isSampling = true;
        if (this.isLoggedIn) {
          console.log('[Battery Manual] Error recovery: Resuming IMU sampling...');
          this.requestNextSample();
        }
      }
      throw err;
    }
  }

  /**
   * Test warning function (L command)
   * @returns {Promise<void>}
   */
  async testWarning() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      await this.serial.writeCommand('L');
      console.log('✓ Warning test triggered');
    } catch (err) {
      console.error('Test warning failed:', err);
      throw new Error(`Failed to test warning: ${err.message}`);
    }
  }

  /**
   * Test alarm function (O command)
   * @returns {Promise<void>}
   */
  async testAlarm() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      await this.serial.writeCommand('O');
      console.log('✓ Alarm test triggered');
    } catch (err) {
      console.error('Test alarm failed:', err);
      throw new Error(`Failed to test alarm: ${err.message}`);
    }
  }

  /**
   * Cancel alarm (X command)
   * @returns {Promise<void>}
   */
  async cancelAlarm() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      await this.serial.writeCommand('X');
      console.log('✓ Alarm cancelled');
    } catch (err) {
      console.error('Cancel alarm failed:', err);
      throw new Error(`Failed to cancel alarm: ${err.message}`);
    }
  }

  /**
   * Calibrate device (^ command)
   * Device must be level and stationary
   * @returns {Promise<void>}
   */
  async calibrate() {
    if (!this.isLoggedIn) {
      throw new Error('Not logged in to device');
    }

    try {
      await this.serial.writeCommand('^');
      console.log('✓ Calibration initiated');
      console.log('  Ensure device is level and engine is off');
    } catch (err) {
      console.error('Calibration failed:', err);
      throw new Error(`Failed to calibrate: ${err.message}`);
    }
  }

  /**
   * Get connection status
   * @returns {boolean}
   */
  isConnected() {
    return this.serial.isConnected && this.isLoggedIn;
  }

  /**
   * Add event listener to serial connection
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    this.serial.on(event, callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    this.serial.off(event, callback);
  }
}
